import { useEffect, useState, useCallback } from 'react'
import api from '../services/api'

// Firm-wide feature toggle system.  Different from per-user permissions:
// permissions are "you can access this"; feature flags are "this feature
// exists at this firm at all."  Effective visibility = feature_enabled AND
// user_has_permission.  Missing keys default to enabled (backward compat).
//
// The full catalog of togglable features + their user-facing labels lives
// here as the single source of truth, so Firm Settings can render toggles
// and Layout/route guards can enforce them from the same list.
//
// `noEffect` marks a switch that nothing reads yet (verified 2026-10-12 by
// grepping api/src and frontend/src for every key: no isEnabled /
// isFeatureOn / requireFeature / feature= / navItems gate names it).  Settings
// → Features labels it as having no effect instead of hiding or removing it,
// so a stored value is kept and nobody is told a toggle did something it
// did not.  Drop the field in the same change that makes the app read the
// key (lib/featureCatalog.test.js re-checks the source for readers).
export const FEATURE_CATALOG = [
  { key: 'case_analysis',  label: 'Case Analysis',       description: 'CaseMap-style fact/issue tracking on matter detail — chronology, cast, questions, ReportBook, MSJ brief, etc.' },
  { key: 'discovery',      label: 'Discovery Tracking',  description: 'ROG/RFP/depo tracking with CCP/FRCP deadline auto-calculation, MTC clock, privilege log' },
  { key: 'court_rules',    label: 'Court Rules',         description: 'Courts directory, deadline calculator, chamber rules per judge, PDF extraction admin' },
  { key: 'sol_tracker',    label: 'SOL Tracker',         description: 'Statute of Limitations tracking per matter with tolling + auto-calc' },
  { key: 'conflicts',      label: 'Conflict Checker',    description: 'Cross-record conflict-of-interest search across clients/contacts/matters' },
  { key: 'communications', label: 'Communications Log',  description: 'Phone/email/letter/meeting log per matter' },
  { key: 'trust',          label: 'Trust Accounting',    description: 'IOLTA-compliant trust accounting with Cal Rule 1.15 tools (leaves permissions intact if you use it)' },
  { key: 'ctapp',          label: 'CTAPP Reporting',     description: 'California CTAPP annual reporting + filing tracker (Trust page tabs)',
    noEffect: 'No effect yet — turning this off hides nothing: the CTAPP Report (Trust → Compliance) still shows to everyone who can open Trust.' },
  { key: 'portal',         label: 'Client Portal',       description: 'Public client portal for viewing invoices, matters, trust balances' },
  { key: 'batch_invoicing',label: 'Batch Invoicing',     description: 'End-of-month batch invoice generation wizard' },
  { key: 'discovery_import',label:'Discovery CSV Import',description: 'Excel/CSV bulk import of discovery sets on matter detail (leave off if you want everything hand-entered)',
    noEffect: 'No effect yet — turning this off hides nothing: the Import Excel button still shows in a matter\'s Discovery section.' },
  { key: 'custom_reports', label: 'Custom Report Builder', description: 'Admin-defined ad-hoc reports over time-entries, invoices, matters, or clients.  Turn off if the pre-built Reports page is enough.' },
  { key: 'attorney_productivity', label: 'Attorney Productivity Dashboard', description: 'Server-computed per-attorney rollup with sparklines + firm-average comparison.  Redundant if you rely on the standard Attorney Productivity report.' },
  { key: 'passkeys',       label: 'Passkey (WebAuthn) login', description: 'Enroll a device / Touch ID / YubiKey as an alternative to password login.  Requires WEBAUTHN_RPID + WEBAUTHN_ORIGIN env vars.' },
  { key: 'realtime',       label: 'Realtime WebSocket toasts', description: 'Live toasts when someone else records a payment, communication, deposit, etc.  Firms that prefer polling can disable this.' },
  { key: 'broadcasts',     label: 'Admin firm-wide broadcast', description: 'Admin can push a firm-wide toast to everyone connected.  Turn off if you don\'t want that surface.' },
  { key: 'witnesses',      label: 'Witness List (per matter)', description: 'Per-matter CCP §2034 witness disclosure roster (percipient + retained/non-retained experts).  Complements the Case Analysis Cast tab; distinct because Cast is theory-of-case work-product and Witnesses is the formal disclosure roster served on opposing counsel.' },
]

// Cache the last-loaded flags in module scope so route guards + nav + settings
// all see the same snapshot, and a re-render doesn't refetch.  Cleared on
// PUT /api/settings via the `feature-flags-updated` window event.
let _cache = null

function readFromCache() { return _cache }
function writeCache(flags) { _cache = flags || {} }

export function useFeatureFlags() {
  const [flags, setFlags] = useState(() => readFromCache() || {})
  const [loaded, setLoaded] = useState(() => readFromCache() !== null)

  useEffect(() => {
    if (loaded) return
    let cancelled = false
    api.get('/api/settings').then((res) => {
      if (cancelled) return
      const f = (res.data && typeof res.data.feature_flags === 'object') ? res.data.feature_flags : {}
      writeCache(f)
      setFlags(f)
      setLoaded(true)
    }).catch(() => {
      // Fail-soft: assume everything enabled when we can't reach settings.
      writeCache({})
      setLoaded(true)
    })
    return () => { cancelled = true }
  }, [loaded])

  // Cross-component invalidation — Settings dispatches this after PUT so
  // Layout + everyone else re-reads without a hard reload.
  useEffect(() => {
    const refresh = () => {
      api.get('/api/settings').then((res) => {
        const f = (res.data && typeof res.data.feature_flags === 'object') ? res.data.feature_flags : {}
        writeCache(f)
        setFlags(f)
      }).catch(() => {})
    }
    window.addEventListener('feature-flags-updated', refresh)
    return () => window.removeEventListener('feature-flags-updated', refresh)
  }, [])

  // Missing key = enabled (backward compat).  Explicit false = disabled.
  const isEnabled = useCallback((key) => flags[key] !== false, [flags])

  return { flags, isEnabled, loaded }
}

// Static check for use outside a React tree (e.g. sync route decisions).
// Returns true if the cache hasn't been loaded yet — the network fetch will
// catch up on next render.
export function isFeatureEnabled(key) {
  if (!_cache) return true
  return _cache[key] !== false
}
